import React, { useState, useRef, useEffect } from 'react';
import { 
  Camera, 
  ScanLine, 
  X, 
  Loader, 
  CheckCircle, 
  AlertCircle,
  Keyboard,
  Zap,
  Package
} from 'lucide-react';
import { barcodeApi, type BarcodeProduct, type BarcodeScanResult } from '../../lib/barcodeApi';

interface BarcodeScannerProps {
  onProductFound: (product: BarcodeProduct) => void;
  onClose: () => void;
  isOpen: boolean;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  onProductFound,
  onClose,
  isOpen
}) => {
  const [scanMode, setScanMode] = useState<'camera' | 'manual'>('manual');
  const [manualBarcode, setManualBarcode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<BarcodeScanResult | null>(null);
  const [error, setError] = useState<string>('');
  const [cameraSupported, setCameraSupported] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    // Check if camera is supported
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      setCameraSupported(true);
    }
  }, []);

  useEffect(() => {
    if (isOpen && scanMode === 'camera' && cameraSupported) {
      startCamera();
    }
    
    return () => {
      stopCamera();
    };
  }, [isOpen, scanMode, cameraSupported]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Use back camera
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Unable to access camera. Please use manual input.');
      setScanMode('manual');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const handleManualScan = async () => {
    if (!manualBarcode.trim()) {
      setError('Please enter a barcode');
      return;
    }

    await performBarcodeLookup(manualBarcode.trim());
  };

  const performBarcodeLookup = async (barcode: string) => {
    setIsScanning(true);
    setError('');
    setScanResult(null);

    try {
      const result = await barcodeApi.lookupBarcode(barcode);
      setScanResult(result);

      if (result.found && result.product) {
        // Auto-select the product after a brief delay to show success
        setTimeout(() => {
          onProductFound(result.product!);
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError('Barcode lookup failed. Please try again.');
    } finally {
      setIsScanning(false);
    }
  };

  const handleSuggestionSelect = (product: BarcodeProduct) => {
    onProductFound(product);
    onClose();
  };

  const resetScanner = () => {
    setScanResult(null);
    setError('');
    setManualBarcode('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <ScanLine className="w-5 h-5 mr-2 text-blue-600" />
            Barcode Scanner
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode Selection */}
        <div className="p-4 border-b">
          <div className="flex space-x-2">
            <button
              onClick={() => setScanMode('manual')}
              className={`flex-1 flex items-center justify-center py-2 px-3 rounded-lg text-sm font-medium ${
                scanMode === 'manual'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Keyboard className="w-4 h-4 mr-2" />
              Manual Entry
            </button>
            <button
              onClick={() => setScanMode('camera')}
              disabled={!cameraSupported}
              className={`flex-1 flex items-center justify-center py-2 px-3 rounded-lg text-sm font-medium ${
                scanMode === 'camera'
                  ? 'bg-blue-600 text-white'
                  : cameraSupported
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-gray-50 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Camera className="w-4 h-4 mr-2" />
              Camera
            </button>
          </div>
        </div>

        {/* Scanner Content */}
        <div className="p-4">
          {scanMode === 'manual' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Barcode Number
                </label>
                <input
                  type="text"
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  placeholder="e.g., 012345678905"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleManualScan()}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the numbers from the barcode (8-14 digits)
                </p>
              </div>
              
              <button
                onClick={handleManualScan}
                disabled={isScanning || !manualBarcode.trim()}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isScanning ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Scan Barcode
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Camera View */}
              <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  className="w-full h-48 object-cover"
                  playsInline
                  muted
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-24 border-2 border-blue-500 rounded-lg"></div>
                </div>
                <div className="absolute bottom-2 left-2 right-2">
                  <p className="text-white text-xs text-center bg-black bg-opacity-50 rounded px-2 py-1">
                    Position barcode within the frame
                  </p>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 text-center">
                Camera scanning is experimental. Use manual entry for best results.
              </p>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center text-red-800">
                <AlertCircle className="w-4 h-4 mr-2" />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Scan Results */}
          {scanResult && (
            <div className="mt-4 space-y-3">
              {scanResult.found && scanResult.product ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-green-900">{scanResult.product.name}</h4>
                      {scanResult.product.brand && (
                        <p className="text-sm text-green-700">Brand: {scanResult.product.brand}</p>
                      )}
                      <p className="text-xs text-green-600 mt-1">
                        Source: {scanResult.product.data_source} (Confidence: {scanResult.product.confidence_score}%)
                      </p>
                      {scanResult.product.nutrition && (
                        <div className="mt-2 text-xs text-green-700">
                          <span className="font-medium">Nutrition per serving:</span> {scanResult.product.nutrition.calories} cal, 
                          {scanResult.product.nutrition.carbs}g carbs, {scanResult.product.nutrition.protein}g protein
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center text-yellow-800">
                      <Package className="w-4 h-4 mr-2" />
                      <span className="text-sm">{scanResult.error || 'Product not found in database'}</span>
                    </div>
                  </div>

                  {/* Suggestions */}
                  {scanResult.suggestions && scanResult.suggestions.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-900 mb-2">Similar Products:</h5>
                      <div className="space-y-2">
                        {scanResult.suggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => handleSuggestionSelect(suggestion)}
                            className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200"
                          >
                            <div className="font-medium text-gray-900">{suggestion.name}</div>
                            {suggestion.brand && (
                              <div className="text-sm text-gray-600">{suggestion.brand}</div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                              {suggestion.data_source} • {suggestion.confidence_score}% match
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={resetScanner}
                className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200"
              >
                Scan Another Barcode
              </button>
            </div>
          )}
        </div>

        {/* Hidden canvas for image processing */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
};

export default BarcodeScanner; 
